import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {Github, Eye, Code, Globe, Zap } from 'lucide-react';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

const featuredProjects = [
    {
      title: "SmartKheti",
      description: "An intelligent agricultural management system that helps farmers optimize crop yields through AI-powered insights, weather forecasting, and automated irrigation control.",
      image: "/images/project1.png",
      tech: ["Python", "Django", "DRF", "React", "PostgreSQL", "Tailwind CSS", "Google Teachable Machine", "AI model", 'Tensorflow', "Render", "Vercel", "Cloudinary"],
      liveUrl: "https://smartkheti.vercel.app/",
      githubUrl: "https://github.com/Bhushan791/SmartKheti",
      features: ["AI Crop Disease Analysis", "Live Weather & News Integration ", "Report Analytics", "Crop Marketplace"]
    },
    {
      title: "Vetau - Lost & Found",
      description: "A mobile app that connects people when items are lost or found. Users can post lost/found items, claim them, chat in real-time, and receive rewards for returning items. Features backend API development, real-time communication, and comprehensive admin panel.",
      image: "/images/project3.png",
      tech: ["Node.js", "MongoDB", "Flutter", "Firebase", "Socket.io", "TURF", "Cloudinary", "Render", "MongoDB Atlas", "React", "Tailwind CSS", "Vercel", "Riverpod"],
      liveUrl: "#",
      githubUrl: "https://github.com/Bhushan791/Vetau",
      features: ["Real-time Chat", "Lost & Found Posts", "Item Claims", "Reward System", "Admin Panel", "Firebase Auth"]
    },
    {
      title: "Smart Vacuum Robot",
      description: "An autonomous vacuum robot built during a 10-day IoT & Robotics Workshop. Uses ultrasonic sensors for obstacle detection, DC motors for navigation, and a fan-powered vacuum system for real-time cleaning. Completed in 3 days as a team project.",
      image: "/images/project4.png",
      tech: ["Arduino", "Ultrasonic Sensor", "Servo Motor", "L298N Motor Driver", "DC Motors", "Fan Module", "C++"],
      liveUrl: "#",
      githubUrl: "#",
      features: ["Obstacle Detection", "Autonomous Navigation", "Real-time Cleaning", "Intelligent Turns", "Servo Scanning"]
    },
    {
      title: "Anonymous Chat App",
      description: "A secure real-time messaging platform with no messages saving, anonymous chat room, and advanced privacy for safe chatting & media sharing.",
      image: "/images/project2.png",
      tech: ["HTML", "CSS", 'JS', "Socket.io", "WebRTC", "ffmpeg","Render"],
      liveUrl: "https://anomochat.onrender.com/",
      githubUrl: "https://github.com/Bhushan791/Chat_app",
      features: ["Real-time Chat", "No Login", "No message save", "Anonymous Room", "File Sharing", "Voice Messages"]
    }
  ];

  const otherProjects = [
    {
      title: "Taxi Booking System",
      description: "Full-stack taxi booking platform with admin site, payment integration, and customer-rider features.",
      tech: ["Python", "Tkinter", "MySQL",],
      githubUrl: "//github.com/Bhushan791"
    },
    {
      title: "Primary Math Tutor",
      description: "Interactive educational app for children to learn mathematics through gamified lessons and progress tracking with time to time testing throw quiz.",
      tech: ["Java", "MySQL", "JavaFx", "Maven"],
      githubUrl: "#"
    },
    {
      title: "Python Code Editor",
      description: "Online Python code editor with Clean GUI and Fast Execution.",
      tech: ["HTML", "CSS", "JS", "Flask", "SubProcesss"],
      githubUrl: "#"
    },
    {
      title: "Clothing Store",
      description: "Modern fashion e-commerce platform with shopping cart, payment processing, Delivery Tracking and admin dashboard.",
      tech: ["Django", "DRF", "React", "Tailwind",  "PostgreSQL", ],
      githubUrl: "#"
    },
    {
      title: "IoT- Greeting Robot",
      description: "A smart Arduino-based Greeting Robot that detects nearby visitors and responds with a 'Namaste' gesture and voice greeting, showcasing real-world IoT and automation.",
    tech: ["Arduino", "Ultrasonic Sensor", "Servo Motor", "Speaker Module", "SD Card Module", "Rechargeable Battery", "Python"],
      githubUrl: "#"
    },
  {
  title: "Student Management System",
  description: "A comprehensive platform that allows student enrollment, attendance tracking, personal and global notifications, and automated grading and marks management.",
  tech: ["Django", "HTML", "CSS", "JavaScript"],
  githubUrl: "#"
}


  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="projects" className="py-20 px-4 relative">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-primary-gold/5 to-primary-violet/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title gradient-text">Featured Projects</h2>
          <p className="section-subtitle">Showcasing my best work and technical expertise</p>
        </motion.div>

        {/* Featured Projects */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-8 mb-20"
        >
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.title}
              variants={itemVariants}
              className="group relative"
            >
              <div className="project-card h-full">
                {/* Project Image */}
                <div className="relative mb-6 overflow-hidden rounded-xl">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full aspect-video object-cover rounded-xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full aspect-video bg-gradient-to-br from-primary-gold/10 to-primary-violet/10 rounded-xl hidden items-center justify-center">
                    <Code className="w-16 h-16 text-primary-gold/30" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="flex gap-4">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-3 bg-primary-gold/20 backdrop-blur rounded-full hover:bg-primary-gold/30 transition-colors"
                        onClick={() => window.open(project.liveUrl, '_blank')}
                      >
                        <Globe className="w-5 h-5 text-primary-gold" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-3 bg-primary-gold/20 backdrop-blur rounded-full hover:bg-primary-gold/30 transition-colors"
                        onClick={() => window.open(project.githubUrl, '_blank')}
                      >
                        <Github className="w-5 h-5 text-primary-gold" />
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Project Content */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-primary-text group-hover:text-gradient transition-colors">
                    {project.title}
                  </h3>
                  
                  <p className="text-primary-muted leading-relaxed">
                    {project.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {project.features.map((feature) => (
                      <span
                        key={feature}
                        className="px-3 py-1 bg-primary-gold/10 text-primary-gold text-sm rounded-full border border-primary-gold/20"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-white/5 text-primary-muted text-sm rounded-full border border-white/10"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open(project.liveUrl, '_blank')}
                      className="btn-primary flex items-center gap-2 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Live Preview
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open(project.githubUrl, '_blank')}
                      className="btn-secondary flex items-center gap-2 text-sm"
                    >
                      <Github className="w-4 h-4" />
                      View Code
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Other Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold text-primary-text mb-4">Other Projects</h3>
          <p className="text-primary-muted">More of my work and experiments</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {otherProjects.map((project, index) => (
            <motion.div
              key={project.title}
              variants={itemVariants}
              className="group"
            >
              <div className="project-card h-full">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-xl font-bold text-primary-text group-hover:text-gradient transition-colors">
                    {project.title}
                  </h4>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 12 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => window.open(project.githubUrl, '_blank')}
                    className="p-2 rounded-lg bg-primary-gold/10 hover:bg-primary-gold/20 transition-colors"
                  >
                    <Github className="w-5 h-5 text-primary-gold" />
                  </motion.button>
                </div>
                
                <p className="text-primary-muted text-sm leading-relaxed mb-4">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-white/5 text-primary-muted text-xs rounded-full border border-white/10"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="glass-effect p-8 rounded-2xl max-w-2xl mx-auto">
            <Zap className="w-12 h-12 text-primary-gold mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-primary-text mb-4">
              Want to see more?
            </h3>
            <p className="text-primary-muted mb-6">
              Check out my GitHub for more projects, experiments, and open-source contributions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open('//github.com/Bhushan791', '_blank')}
              className="btn-primary flex items-center gap-2 mx-auto"
            >
              <Github className="w-5 h-5" />
              Visit My GitHub
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;